import Konva from "konva";
import { KonvaEventObject } from "konva/lib/Node";
import { Vector2d } from "konva/lib/types";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Group } from "react-konva";

import { StrokeWidth } from "components/detector/NotesModal/NotesContent/NotesSection/CustomEditor/ImageUploader/ImageAnnotatorModal/ImageAnnotator/AnnotatorTools/StrokeSettings/StrokeSettings";
import { type ImageSettings } from "context/ImageSettingsContext/imageSettingsContext";
import { CanvasRegion, CanvasAnnotationBoundsUpdate, CanvasAnnotationUpdate } from "types/imageAnnotations";

import {
  ANNOTATION_TEXT_MIN_HEIGHT,
  ANNOTATION_TEXT_MIN_WIDTH,
  DEFAULT_MIN_HEIGHT,
  DEFAULT_MIN_WIDTH,
} from "../Canvas";
import { BoundingBoxNode, Point } from "../types";
import { clamp, parseGeometryToPoints, rotateDimensions } from "../utils";

import { Bounds, denormalizeBox, normalizeBox, ShapeRenderer, ShapeType } from "./ShapeRenderer";
import { DeleteIcon, Label, SelectedGradient } from "./TransformerElements";
import TransformerLayer from "./TransformerLayer";

const BOUDING_BOX_TINY_SIDE_LENGTH = 50;
const MINIMUM_NODE_BORDER_WIDTH = 5;

export const getShadowProps = (zoom: number) => ({
  shadowOffset: {
    x: 0.5 / zoom,
    y: 0.5 / zoom,
  },
  shadowBlur: 3 / zoom,
  shadowOpacity: 0.7,
  shadowColor: "black",
});

type BoundingBoxProps = {
  annotationId?: string;
  isSelected: boolean;
  label?: string;
  strokeColor?: string;
  onDelete?: (annotationId: string) => void;
  onClick?: (event: KonvaEventObject<MouseEvent>, annotationId: string) => void;
  /** Settings coming from the toolbar. This has to be passed as props since context don't work inside Konva */
  imageSettings?: ImageSettings;
  start: Point;
  end: Point;
  isDragging?: boolean;
  canInteract?: boolean;
  setHoveredNode: (node: BoundingBoxNode) => void;
  updateBoundingBox: (elementUpdated: CanvasAnnotationUpdate) => void;
  canvasNormalDimensions: {
    width: number;
    height: number;
  };
  canvasDimensions: {
    width: number;
    height: number;
  };
  canvasPosition: Point;
  zoom: number;
  imageZoomDifference: number;

  /**
   * @default rectangle
   */
  shape?: ShapeType;
  hideMiddleCross?: boolean;
  hideDelete?: boolean;
  strokeWidth?: StrokeWidth;
  text?: string;
  fontSize?: number;
  isDoubleClicked?: boolean;
  onDoubleClick?: () => void;
};

export const DEFAULT_STROKE_WIDTH = 2;

function BoundingBox(props: BoundingBoxProps) {
  const {
    annotationId,
    isSelected,
    label,
    strokeColor = "#00C7D9",
    onDelete,
    onClick,
    imageSettings,
    start: initialStart,
    end: initialEnd,
    isDragging,
    canInteract = true,
    setHoveredNode,
    updateBoundingBox,
    canvasDimensions,
    canvasPosition,
    zoom,
    imageZoomDifference,
    canvasNormalDimensions,
    shape = "rectangle",
    hideMiddleCross,
    hideDelete,
    strokeWidth = DEFAULT_STROKE_WIDTH,
    text,
    fontSize,
    isDoubleClicked,
    onDoubleClick,
  } = props;

  // Get the rotation value from imageSettings if available
  const rotationValue = imageSettings?.rotation ?? 0;
  const isRotated = rotationValue === 90 || rotationValue === 270;

  // Use refs for tracking during drag operations to reduce state updates and re-renders
  const dragPositionRef = useRef<{
    start: Point;
    end: Point;
    currentBBoxPosition: CanvasAnnotationUpdate;
  }>({
    start: initialStart,
    end: initialEnd,
    currentBBoxPosition: {},
  });

  // Keep state for final positions to trigger renders when needed
  const [draggingStart, setDraggingStart] = useState(initialStart);
  const [draggingEnd, setDraggingEnd] = useState(initialEnd);

  const start = isSelected ? draggingStart : initialStart;
  const end = isSelected ? draggingEnd : initialEnd;

  useEffect(() => {
    setDraggingStart(initialStart);
    setDraggingEnd(initialEnd);
    dragPositionRef.current.start = initialStart;
    dragPositionRef.current.end = initialEnd;
  }, [initialStart, initialEnd, isSelected]);

  // Updating the current position of the bounding box in the ref
  const dimensionX = isRotated ? canvasNormalDimensions.height : canvasNormalDimensions.width;
  const dimensionY = isRotated ? canvasNormalDimensions.width : canvasNormalDimensions.height;

  // This method is used to update the bounding box when the user is dragging it
  // When the user releases the mouse, we'll update the main list of annotations
  const handleUpdateBoundingBox = useCallback(
    (elementUpdated: CanvasAnnotationBoundsUpdate) => {
      const { bounds, startDirection } = normalizeBox(start, end);
      const boundsUpdated: Bounds = {
        left: elementUpdated.left ?? bounds.left,
        top: elementUpdated.top ?? bounds.top,
        right: elementUpdated.right ?? bounds.right,
        bottom: elementUpdated.bottom ?? bounds.bottom,
      };
      const denormalizedBox = denormalizeBox(boundsUpdated, startDirection);

      // Updating start and end points of the bounding box in the ref
      const region: CanvasRegion = {
        start: {
          x: denormalizedBox.start.x / dimensionX,
          y: denormalizedBox.start.y / dimensionY,
        },
        end: {
          x: denormalizedBox.end.x / dimensionX,
          y: denormalizedBox.end.y / dimensionY,
        },
      };

      // Store in ref to avoid state updates during dragging
      dragPositionRef.current.currentBBoxPosition = region;

      const parsedGeometry = parseGeometryToPoints(region, rotateDimensions(canvasNormalDimensions, isRotated));
      dragPositionRef.current.start = parsedGeometry.start;
      dragPositionRef.current.end = parsedGeometry.end;

      // Update state for rendering - in a real optimization, we might only do this on drag end
      // but for now we'll keep the visual updates to ensure the UI remains responsive
      setDraggingStart(parsedGeometry.start);
      setDraggingEnd(parsedGeometry.end);
    },
    [canvasNormalDimensions, isRotated, dimensionX, dimensionY, start, end]
  );

  const handleClick = useCallback(
    (event: KonvaEventObject<MouseEvent>) => {
      if (annotationId) onClick?.(event, annotationId);
    },
    [annotationId, onClick]
  );

  // Memoize expensive calculations to improve performance
  const position = useMemo(
    () => ({ x: Math.min(start.x, end.x), y: Math.min(start.y, end.y) }),
    [start.x, start.y, end.x, end.y]
  );

  const dimensions = useMemo(
    () => ({
      width: Math.abs(start.x - end.x),
      height: Math.abs(start.y - end.y),
    }),
    [start.x, end.x, start.y, end.y]
  );

  const zoomedDimensions = useMemo(
    () => ({
      width: dimensions.width * zoom,
      height: dimensions.height * zoom,
    }),
    [dimensions.width, dimensions.height, zoom]
  );

  const center = useMemo(
    () => ({
      x: position.x + dimensions.width / 2,
      y: position.y + dimensions.height / 2,
    }),
    [position.x, position.y, dimensions.width, dimensions.height]
  );

  // In the Bounding box there are elements that decorate the box, like the corners, top, bottom, left and right
  // and this defines the percentage of the width that these elements will take. This is to avoid having the elements
  // too big when the bounding box is small.
  const nodeElementsPercentage = 0.5;

  const edgeWidth = useMemo(() => 2 / zoom, [zoom]);

  const nodeBorderWidth = useMemo(() => {
    const width = Math.min(5 / zoom, dimensions.width * nodeElementsPercentage);
    return Math.max(MINIMUM_NODE_BORDER_WIDTH, width);
  }, [dimensions.width, zoom, nodeElementsPercentage]);

  const nodeSize = useMemo(() => {
    const width = Math.min(Math.min(dimensions.width, dimensions.height) * nodeElementsPercentage, 15 / zoom);
    return Math.max(MINIMUM_NODE_BORDER_WIDTH, width);
  }, [dimensions.height, dimensions.width, zoom, nodeElementsPercentage]);

  const halfNodeBorderWidth = useMemo(() => nodeBorderWidth / 2, [nodeBorderWidth]);

  const boundingBoxRef = useRef<Konva.Rect>(null);

  const handleNodes = useMemo(
    () => ({
      topLeft: {
        x: position.x,
        y: position.y,
      },
      topMiddle: {
        x: position.x + dimensions.width / 2,
        y: position.y,
      },
      topRight: {
        x: position.x + dimensions.width,
        y: position.y,
      },
      rightMiddle: {
        x: position.x + dimensions.width,
        y: position.y + dimensions.height / 2,
      },
      bottomRight: {
        x: position.x + dimensions.width,
        y: position.y + dimensions.height,
      },
      bottomMiddle: {
        x: position.x + dimensions.width / 2,
        y: position.y + dimensions.height,
      },
      bottomLeft: {
        x: position.x,
        y: position.y + dimensions.height,
      },
      leftMiddle: {
        x: position.x,
        y: position.y + dimensions.height / 2,
      },
    }),
    [position.x, position.y, dimensions.width, dimensions.height]
  );

  const isBoundingBoxTiny = useMemo(
    () => dimensions.width <= BOUDING_BOX_TINY_SIDE_LENGTH || dimensions.height <= BOUDING_BOX_TINY_SIDE_LENGTH,
    [dimensions.width, dimensions.height]
  );

  // This is the main bbox hit zone
  const boxHitbox = isBoundingBoxTiny ? 5 : 10;

  // This is very important to resize using the corners or the sides of the bounding box.
  const nodeHitbox = isBoundingBoxTiny ? 2 : 10;

  const PADDING_FOR_FLOATING_ELEMENTS = 20;

  let hasPaddingForFloatingElements =
    position.y - PADDING_FOR_FLOATING_ELEMENTS * 2 >= 0 &&
    position.y + dimensions.height + PADDING_FOR_FLOATING_ELEMENTS * 1.5 <= canvasDimensions.height / zoom;
  if (isRotated) {
    hasPaddingForFloatingElements =
      position.x - PADDING_FOR_FLOATING_ELEMENTS * 2 >= 0 &&
      position.x + dimensions.width + PADDING_FOR_FLOATING_ELEMENTS * 1.5 <= canvasDimensions.height / zoom;
  }
  // Calculate the borders of the canvas in the current coordinate system
  const imageRightBorder = canvasPosition.x + canvasDimensions.width / 2;
  const imageLeftBorder = canvasPosition.x - canvasDimensions.width / 2 + halfNodeBorderWidth;
  const imageTopBorder = canvasPosition.y - canvasDimensions.height / 2 + halfNodeBorderWidth;
  const imageBottomBorder = canvasPosition.y + canvasDimensions.height / 2;

  /**
   * Calculates a coordinate value (x or y) based on rotation and flip states,
   * applying clamping to keep the position within image boundaries.
   *
   * @param {string} axis - The axis to calculate ('x' or 'y')
   * @returns {number} The clamped coordinate value
   */
  const getCoordinate = useCallback(
    (axis: "x" | "y", pos: number) => {
      const isXAxis = axis === "x";

      // Select the appropriate position and border values based on axis
      const position = pos;
      const minBorderBase = isXAxis ? imageLeftBorder : imageTopBorder;
      const maxBorderBase = isXAxis ? imageRightBorder : imageBottomBorder;

      // Determine which dimension to use based on rotation and axis
      let dimensionToUse;
      if ((rotationValue === 0 || rotationValue === 180) === isXAxis) {
        // For X axis with 0°/180° OR Y axis with 90°/270°: use width
        dimensionToUse = zoomedDimensions.width;
      } else {
        // For X axis with 90°/270° OR Y axis with 0°/180°: use height
        dimensionToUse = zoomedDimensions.height;
      }

      // Determine if we need to flip the min/max bounds
      let shouldFlipBounds = false;

      // Determine which flip affects this axis based on rotation
      const relevantFlip =
        (rotationValue === 0 || rotationValue === 180) === isXAxis
          ? imageSettings?.flipX ?? false // For X axis with 0°/180° OR Y axis with 90°/270°: horizontal flip matters
          : imageSettings?.flipY ?? false; // For X axis with 90°/270° OR Y axis with 0°/180°: vertical flip matters

      shouldFlipBounds = relevantFlip;

      // Account for how rotation itself affects the default bound orientation
      if (
        (isXAxis && rotationValue === 180) ||
        (isXAxis && rotationValue === 90) ||
        (!isXAxis && rotationValue === 180) ||
        (!isXAxis && rotationValue === 270)
      ) {
        shouldFlipBounds = !shouldFlipBounds;
      }

      // Set bounds based on whether they should be flipped
      let minBound;
      let maxBound;
      if (shouldFlipBounds) {
        minBound = minBorderBase + dimensionToUse;
        maxBound = maxBorderBase;
      } else {
        minBound = minBorderBase;
        maxBound = maxBorderBase - dimensionToUse;
      }

      return clamp(position, minBound, maxBound);
    },
    [
      imageBottomBorder,
      imageLeftBorder,
      imageRightBorder,
      imageSettings?.flipX,
      imageSettings?.flipY,
      imageTopBorder,
      rotationValue,
      zoomedDimensions.height,
      zoomedDimensions.width,
    ]
  );

  const getDragBoundFunc = useCallback(
    (pos: Vector2d) => ({
      x: getCoordinate("x", pos.x),
      y: getCoordinate("y", pos.y),
    }),
    [getCoordinate]
  );

  const relativeStrokeWidth = strokeWidth / zoom;

  return (
    <Group
      onDragEnd={() => {
        // Use the ref values when drag ends to ensure we have the latest position
        updateBoundingBox(dragPositionRef.current.currentBBoxPosition);

        // Batch draw to optimize rendering
        const layer = boundingBoxRef.current?.getLayer();
        if (layer) {
          layer.batchDraw();
        }
      }}
    >
      {isSelected && <SelectedGradient dimensions={dimensions} position={position} />}
      <ShapeRenderer
        ref={boundingBoxRef}
        text={text}
        imageZoomDifference={imageZoomDifference}
        updateAnnotation={updateBoundingBox}
        isDoubleClicked={isDoubleClicked}
        onDblClick={onDoubleClick}
        isSelected={isSelected}
        type={shape}
        dragBoundFunc={getDragBoundFunc}
        draggable={canInteract}
        start={start}
        end={end}
        stroke={strokeColor}
        strokeWidth={relativeStrokeWidth}
        fontSize={fontSize}
        perfectDrawEnabled={false}
        hitStrokeWidth={boxHitbox}
        fillEnabled={false}
        shadowForStrokeEnabled
        onMouseDown={handleClick}
        onClick={handleClick}
        onMouseEnter={() => {
          setHoveredNode(isSelected ? "active-bounding-box" : "bounding-box");
        }}
        onMouseLeave={() => {
          setHoveredNode("none");
        }}
        {...getShadowProps(zoom)}
        onDragMove={({ target }) => {
          if (!canInteract) {
            return;
          }
          const x = target.x();
          const y = target.y();

          handleUpdateBoundingBox({
            left: x,
            top: y,
            right: x + dimensions.width,
            bottom: y + dimensions.height,
          });
        }}
      />

      {/* Use the TransformerLayer component for all transformer elements */}
      <TransformerLayer
        annotationId={annotationId}
        showBoxHandle={shape === "text"}
        isSelected={isSelected}
        color={strokeColor}
        onClick={onClick}
        dimensions={dimensions}
        canInteract={canInteract}
        setHoveredNode={setHoveredNode}
        handleUpdateBoundingBox={handleUpdateBoundingBox}
        zoom={zoom}
        handleNodes={handleNodes}
        center={center}
        imageRightBorder={imageRightBorder}
        imageLeftBorder={imageLeftBorder}
        imageTopBorder={imageTopBorder}
        imageBottomBorder={imageBottomBorder}
        zoomedDimensions={zoomedDimensions}
        nodeSize={nodeSize}
        nodeBorderWidth={nodeBorderWidth}
        halfNodeBorderWidth={halfNodeBorderWidth}
        edgeWidth={edgeWidth}
        nodeHitbox={nodeHitbox}
        isRotated={isRotated}
        hideMiddleCross={hideMiddleCross}
        boxDragBoundFunc={getDragBoundFunc}
        minWidth={shape === "text" ? ANNOTATION_TEXT_MIN_WIDTH : DEFAULT_MIN_WIDTH}
        minHeight={shape === "text" ? ANNOTATION_TEXT_MIN_HEIGHT : DEFAULT_MIN_HEIGHT}
      />

      {isSelected && (
        <>
          {!!label && label.length > 0 && dimensions.width > 0 && dimensions.height > 0 && (
            <Label
              innerPosition={!hasPaddingForFloatingElements}
              label={label}
              position={{ x: position.x, y: position.y }}
              color={strokeColor}
              imageSettings={imageSettings}
              zoom={zoom}
              onMouseEnter={() => {
                setHoveredNode(isSelected ? "active-bounding-box" : "bounding-box");
              }}
              onMouseLeave={() => {
                setHoveredNode("none");
              }}
              onMouseDown={() => {
                // transferring drag to the bounding box
                boundingBoxRef.current?.startDrag();
              }}
              dimensions={dimensions}
              imageRectRef={boundingBoxRef}
            />
          )}

          {canInteract && !hideDelete && onDelete && (
            <DeleteIcon
              innerPosition={!hasPaddingForFloatingElements}
              imageSettings={imageSettings}
              dimensions={dimensions}
              position={position}
              onDelete={() => {
                if (annotationId) onDelete(annotationId);
                setHoveredNode("none");
              }}
              color={strokeColor}
              isDragging={isDragging}
              zoom={zoom}
              onMouseEnter={() => {
                setHoveredNode("close-icon");
              }}
              onMouseLeave={() => {
                setHoveredNode("none");
              }}
            />
          )}
        </>
      )}
    </Group>
  );
}

export default React.memo(BoundingBox);
